import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SeoHelmet from './SeoHelmet';
import { isPrerender } from '../../../utils/seo/prerender.util';

// Every slug this site builds is lowercase (each builder runs slugify with
// lower: true), so a path carrying uppercase letters never names a distinct
// page - it is a typo or an inbound link someone re-cased. Left alone it
// rendered as its own self-canonical duplicate: /catalog/Braking-System
// answered 200 with canonical https://partevo.co.uk/catalog/Braking-System
// (measured 2026-09-21). Returns the lowercase path, or null when the path
// is already canonical.
export const getLowercasePath = (pathname: string): string | null => {
  const lower = pathname.toLowerCase();

  return lower === pathname ? null : lower;
};

type PermanentRedirectProps = {
  to: string;
  // Set when `to` already carries its own query string; by default the
  // current search and hash travel along, so /Catalog?utm=x lands on
  // /catalog?utm=x.
  keepQuery?: boolean;
};

// A redirect crawlers can see. React Router's <Navigate> moves the browser but
// leaves the prerender snapshot as a 200 carrying the TARGET page - two URLs,
// one content, no canonical relation. This emits the prerender meta pair that
// server.js turns into a real HTTP 301 + Location, and moves users with
// replace so the address bar corrects itself.
const PermanentRedirect = ({
  to,
  keepQuery = true,
}: PermanentRedirectProps) => {
  const navigate = useNavigate();
  const { search, hash } = useLocation();
  const target = keepQuery ? `${to}${search}${hash}` : to;

  useEffect(() => {
    if (isPrerender()) {
      return;
    }

    navigate(target, { replace: true });
  }, [target, navigate]);

  return (
    <SeoHelmet title={to} description={to}>
      <meta name="robots" content="noindex" />
      <meta name="prerender-status-code" content="301" />
      <meta
        name="prerender-header"
        content={`Location: ${window.location.origin}${target}`}
      />
    </SeoHelmet>
  );
};

export default PermanentRedirect;
